// Reusable admin layout: sticky header + left sidebar + content area.
// Sidebar nav highlights active route with gold left border.
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, TrendingUp, Briefcase, Layers, Banknote, FileText, Award, DollarSign, Flame, LogOut, Menu, X, Sparkles, Mail, Shield, Gavel,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const NAV = [
  { to: "/dashboard/admin", label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/dashboard/admin/leads", label: "Hot Leads", Icon: Flame },
  { to: "/dashboard/admin/clients", label: "Client Portfolio", Icon: Users },
  { to: "/dashboard/admin/credit", label: "Credit Pipeline", Icon: TrendingUp },
  { to: "/dashboard/admin/credit-analyzer", label: "AI Credit Analyzer", Icon: Sparkles },
  { to: "/dashboard/admin/disputes", label: "Dispute Letters", Icon: Mail },
  { to: "/dashboard/admin/cfpb", label: "CFPB Escalation", Icon: Shield },
  { to: "/dashboard/admin/arbitration", label: "Arbitration", Icon: Gavel },
  { to: "/dashboard/admin/funding", label: "Funding Pipeline", Icon: Briefcase },
  { to: "/dashboard/admin/tradelines", label: "Tradeline Tracker", Icon: Layers },
  { to: "/dashboard/admin/grants", label: "Grant Tracker", Icon: Banknote },
  { to: "/dashboard/admin/documents", label: "Document Queue", Icon: FileText },
  { to: "/dashboard/admin/referrals", label: "Referral Leaderboard", Icon: Award },
  { to: "/dashboard/admin/revenue", label: "Revenue", Icon: DollarSign },
];

export default function AdminLayout({ children, title, kicker = "Admin Console" }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-jc-black text-jc-white">
      {/* top bar */}
      <header data-testid="admin-topbar" className="fixed top-0 inset-x-0 z-40 bg-jc-black/95 backdrop-blur-md border-b border-jc-gold/15">
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setOpen(true)} className="lg:hidden h-9 w-9 inline-flex items-center justify-center border border-jc-gold/40 text-jc-gold" data-testid="admin-mobile-toggle" aria-label="Open admin menu"><Menu size={16} /></button>
            <Link to="/dashboard/admin" className="flex items-center gap-3">
              <span className="inline-block h-7 w-7 border border-jc-gold rotate-45" />
              <span className="font-display text-lg tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> ADMIN</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">{new Date().toLocaleString()}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold">CEO {user?.first_name || "Jace"}</div>
            </div>
            <button data-testid="admin-logout" onClick={logout} className="jc-btn jc-btn-outline !py-2 !px-4 !text-xs"><LogOut size={12} /> LOG OUT</button>
          </div>
        </div>
      </header>

      {/* sidebar (desktop fixed, mobile drawer) */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:top-16 top-0 bottom-0 left-0 z-50 lg:z-30 w-72 bg-jc-navy border-r border-jc-gold/15 transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="lg:hidden flex items-center justify-between px-6 h-16 border-b border-jc-gold/15">
          <span className="font-display text-lg tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> ADMIN</span>
          <button onClick={() => setOpen(false)} className="h-9 w-9 inline-flex items-center justify-center border border-jc-gold/40 text-jc-gold" aria-label="Close menu"><X size={16} /></button>
        </div>
        <nav className="py-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] border-l-2 transition-colors ${
                  isActive ? "border-jc-gold bg-jc-black/40 text-jc-gold-light" : "border-transparent text-jc-muted hover:text-jc-white hover:bg-jc-black/30"
                }`
              }
            >
              <n.Icon size={14} className="opacity-80 group-hover:opacity-100" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 px-6 py-5 border-t border-jc-gold/15 text-[10px] uppercase tracking-[0.25em] font-mono text-jc-muted">
          ◆ JC3 Wealth Legacy LLC
        </div>
      </aside>

      <main className="lg:pl-72 pt-16 min-h-screen">
        <div className="px-6 md:px-10 py-10">
          <div className="mb-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ {kicker}</p>
            <h1 data-testid="admin-page-title" className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: 0.92 }}>
              {title}
            </h1>
          </div>
          {children}
        </div>
      </main>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-jc-black/70" onClick={() => setOpen(false)} />}
    </div>
  );
}

export { NAV as ADMIN_NAV };
